"use strict";
(globalThis["webpackChunktvg_shopify_theme"] ||= []).push([[9784],{

/***/ 5228
(__unused_webpack_module, __unused_webpack___webpack_exports__, __webpack_require__) {

/* unused harmony export GlobalFiltersController */
/* harmony import */ var _hotwired_stimulus__WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__(7809);
/* harmony import */ var _theme_focus_trap__WEBPACK_IMPORTED_MODULE_1__ = __webpack_require__(2959);
/* harmony import */ var _theme_section_rendering__WEBPACK_IMPORTED_MODULE_2__ = __webpack_require__(4957);




class GlobalFiltersController extends _hotwired_stimulus__WEBPACK_IMPORTED_MODULE_0__.Controller {
  static targets = [
    "drawer",
    "overlay",
    "filterValue",
    "filterTags",
    "closeButton",
  ]

  static values = {
    collectionId: String,
    sectionId: String,
    defaultUrl: String,
    closeButtonUrl: String,
  }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)

    if (this.collectionIdValue == "") {
      // Filters can only be accessed from collection pages, so if we're not on a collection page,
      // we need to rerender the section _from_ a collection page, using the default URL.
      // Currently this URL is set to the all collection page, which should always be available.
      (0,_theme_section_rendering__WEBPACK_IMPORTED_MODULE_2__.rerenderSection)(this.sectionIdValue, {}, this.defaultUrlValue)
    } else {
      // Filter tags are rendered by JS so let's go ahead and do an initial render.
      this.updateFilterTags()

      // Add any existing filters from the URL to the filter values
      this.addExistingFiltersFromUrl()
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  handleKeydown(event) {
    if (this.drawerTarget.dataset.open !== 'true') return

    if (event.key === 'Escape') {
      this.close()
      return
    }

    // Focus trap: keep Tab within the drawer
    if (event.key === 'Tab') {
      (0,_theme_focus_trap__WEBPACK_IMPORTED_MODULE_1__.trapFocus)(event, this.drawerTarget)
    }
  }

  close() {
    this.drawerTarget.dataset.open = false
    this.overlayTarget.classList.add("hidden")

    // Wait for the animation to finish then hide the whole element
    setTimeout(() => {
      this.element.classList.add("hidden")
      // Restore focus to the element that opened the filters
      this.previouslyFocusedElement?.focus()
      this.previouslyFocusedElement = null
    }, 300)
  }

  open() {
    // Store the currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement

    this.element.classList.remove("hidden")
    this.overlayTarget.classList.remove("hidden")

    // Slight delay to ensure the opening animation plays
    setTimeout(() => {
      this.drawerTarget.dataset.open = true
      // Move focus to the close button after animation
      this.closeButtonTarget?.focus()
    }, 10)

    window.gtag('event', 'Filter_Opened', {})
  }

  addExistingFiltersFromUrl() {
    //For ease of use, filters in the URL can be added using a `filters` query param with just the value.
    // For example, ?filters=Light,Dark will add both filters.
    // This doesn't work with liquids initial render though, so we need to add them here.
    const url = new URL(window.location.href)
    const filters = url.searchParams.get('filters')
    if (filters) {
      const filtersArray = filters.split(',').map(filter => decodeURIComponent(filter))

      filtersArray.forEach(filter => {
        this.addFilterByValue(filter)
      })

      this.filtersUpdated()
    }
  }

  addFilterByValue(value) {
    this.filterValueTargets.forEach(filterValue => {
      if (filterValue.value === value) {
        filterValue.checked = true
      }
    })

    window.gtag('event', `Filter_Add ${value}`, {})
  }

  filtersUpdated() {
    this.fireFilterUpdateEvent()
    this.updateFilterTags()
  }

  removeFilter(e) {
    // Value can either be a param or a detail. Stimulus weirdly doesn't have a consistent way to access it from
    // within AND outside of the controller. At least not that I can find.
    const value = e.params?.value || e.detail?.value

    this.removeFilterByValue(value)
  }

  removeFilterByValue(value) {
    if (!value) {
      console.error('GlobalFiltersController: removeFilterByValue: value is required')

      return
    }

    this.filterValueTargets.forEach(filterValue => {
      if (filterValue.value === value) {
        filterValue.checked = false
      }
    })

    this.filtersUpdated()

    window.gtag('event', `Filter_Remove ${value}`, {})
  }

  updateFilterTags() {
    // Update the filter tags in the section by JS, since we don't want to rerender this component
    // (Because the user is currently interfacing with it)
    this.filterTagsTarget.innerHTML = ""
    const selectedFilters = this.selectedFilters()
    selectedFilters.forEach(filter => {
      const el = document.createElement("button")
      el.classList.add("bg-secondary-dark-cream", "py-1", "px-2", "rounded-sm", "text-small", "flex", "items-center", "gap-2", "hover:cursor-pointer")
      el.dataset.value = filter.value
      el.dataset.globalFiltersValueParam = filter.value
      el.dataset.action = "global-filters#removeFilter"
      el.setAttribute("aria-label", `Remove ${filter.dataset.label} filter`)
      el.appendChild(document.createElement("img"))
      el.querySelector("img").src = this.closeButtonUrlValue
      el.querySelector("img").alt = ""
      el.querySelector("img").setAttribute("aria-hidden", "true")
      el.querySelector("img").classList.add("size-3")
      const textSpan = document.createElement("span")
      textSpan.setAttribute("aria-hidden", "true")
      textSpan.textContent = filter.dataset.label
      el.appendChild(textSpan)

      this.filterTagsTarget.appendChild(el)
    })

    if (selectedFilters.length > 0) {
      const clearFiltersButton = document.createElement("button")
      clearFiltersButton.innerText = "Clear All"
      clearFiltersButton.classList.add("text-small", "underline!", "cursor-pointer")
      clearFiltersButton.dataset.action = "global-filters#clearFilters"
      clearFiltersButton.setAttribute("aria-label", "Clear all filters")
      this.filterTagsTarget.appendChild(clearFiltersButton)
    }
  }

  clearFilters() {
    this.selectedFilters().forEach(filter => {
      filter.checked = false
    })

    this.filtersUpdated()

    window.gtag('event', 'Filter_Clear_All', {})
  }

  fireFilterUpdateEvent() {
    const updatedFilters = this.selectedFilters().reduce((acc, target) => {
      acc[target.name] = (acc[target.name] || []).concat(target.value)
      return acc
    }, {})

    this.dispatch("filtersUpdated", { detail: { filters: updatedFilters } })
  }

  selectedFilters() {
    return this.filterValueTargets.filter(target => target.checked)
  }
}

Stimulus.register("global-filters", GlobalFiltersController)


/***/ }

},
/******/ __webpack_require__ => { // webpackRuntimeModules
/******/ var __webpack_exec__ = (moduleId) => (__webpack_require__(moduleId))
/******/ __webpack_require__.O(0, [2333], () => (__webpack_exec__(5228)));
/******/ var __webpack_exports__ = __webpack_require__.O();
/******/ }
]);